import { __awaiter } from "tslib";
import debug from "debug";
import "@tensorflow/tfjs-backend-webgl";
import { buildCanvas2dPipeline } from "./pipelines/canvas2d/canvas2dPipeline";
import { buildWebGL2Pipeline } from "./pipelines/webgl2/webgl2Pipeline";
import { Events } from "@auvious/utils";
import { Device } from "@auvious/compatibility";
import { createTFLiteModule } from "./tflite";
const log = debug("auvious:bg-filter");
const fullModelFileName = "segm_full_v679.tflite";
const liteModelFileName = "segm_lite_v681.tflite";
export class ImageUnusable extends Error {
    constructor(originalError) {
        super((originalError === null || originalError === void 0 ? void 0 : originalError.message) || "Image is unusable");
        this.originalError = originalError;
        this.name = (originalError === null || originalError === void 0 ? void 0 : originalError.name) || "ImageUnusable";
    }
}
export class AnyError extends Error {
    constructor(originalError) {
        super((originalError === null || originalError === void 0 ? void 0 : originalError.message) || "Background Filter error");
        this.originalError = originalError;
        this.name = (originalError === null || originalError === void 0 ? void 0 : originalError.name) || "BGFilterError";
    }
}
export class BackgroundFilter {
    constructor(timers) {
        this.timers = timers;
        this.events = new Events();
        this.name = "background-filter.video";
        /** control the render loop */
        this.rendering = false;
        this.streamOut = null;
        this.canvasStream = null;
        this.image = null;
        this.tflite = null;
        this.pipeline = null;
        this.modelFileName = fullModelFileName;
        this.backgroundConfig = { type: "blur" };
        this.segmentationConfig = {
            model: "meet",
            inputResolution: "144p",
            backend: "wasm",
            pipeline: "webgl2",
        };
        this.useWebGL = false;
        this.postProcessingConfig = {
            smoothSegmentationMask: true,
            jointBilateralFilter: { sigmaSpace: 1, sigmaColor: 0.1 },
            coverage: [0.4, 0.65],
            lightWrapping: 0.2,
            blendMode: "screen",
        };
        this.anchor = 0;
        this.counter = 0;
        // ms per frame of input source to make sure to not be exceeded
        this.targetTimePerFrame = 0;
        this.hasUnloaded = false;
        this.canvas = document.createElement("canvas");
        this.video = document.createElement("video");
    }
    /** plays only if input stream is set */
    play() {
        if (!this.rendering) {
            this.rendering = true;
            this.render();
        }
        else {
            log("already active or no input stream");
        }
        return this.streamOut;
    }
    pause() {
        this.rendering = false;
    }
    setInput(stream) {
        var _a, _b, _c, _d;
        const oldVideoTrack = (_a = this.streamIn) === null || _a === void 0 ? void 0 : _a.getVideoTracks()[0];
        const videoTrack = stream === null || stream === void 0 ? void 0 : stream.getVideoTracks()[0];
        this.streamIn = stream;
        if ((videoTrack === null || videoTrack === void 0 ? void 0 : videoTrack.readyState) === "live" && videoTrack !== oldVideoTrack) {
            const { width, height } = videoTrack.getSettings();
            if (((_c = (_b = this.canvasStream) === null || _b === void 0 ? void 0 : _b.getVideoTracks()[0]) === null || _c === void 0 ? void 0 : _c.readyState) === "ended" ||
                (this.pipeline &&
                    (width !== this.video.width || height !== this.video.height))) {
                (_d = this.pipeline) === null || _d === void 0 ? void 0 : _d.cleanUp();
                this.pipeline = null;
            }
            else {
                this.video.srcObject = new MediaStream([videoTrack]);
            }
            this.play();
        }
        else {
            if ((videoTrack === null || videoTrack === void 0 ? void 0 : videoTrack.readyState) !== "live") {
                this.pause();
            }
            this.genOutput();
        }
    }
    onOutput(callback) {
        this.events.on("output", callback);
        return () => this.events.off("output", callback);
    }
    onUnload(callback) {
        this.events.on("unload", callback);
        return () => this.events.off("unload", callback);
    }
    /**
     * load and initialize models
     * @param options assetsPath: absolute url where wasm and models reside
     * @returns boolean
     */
    load(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.tflite) {
                return true;
            }
            if (options.assetsPath.endsWith("/")) {
                options.assetsPath = options.assetsPath.slice(0, -1);
            }
            if (options.lite) {
                this.modelFileName = liteModelFileName;
                this.segmentationConfig.inputResolution = "96p";
            }
            if (options.image) {
                const isImageReady = yield this.whenImageReady(options.image);
                if (isImageReady instanceof Error) {
                    this.events.emit("fail", new ImageUnusable(isImageReady));
                    options.image = undefined;
                }
                else {
                    this.backgroundConfig.type = "image";
                    this.image = options.image;
                }
            }
            this.useWebGL =
                (options.useWebgl === undefined || options.useWebgl) &&
                    Device.isWebglSupported;
            if (!(yield this.loadWasm(options.assetsPath))) {
                return false;
            }
            yield this.loadModel(options.assetsPath);
            return true;
        });
    }
    genOutput() {
        var _a, _b, _c;
        this.streamOut =
            ((_b = (_a = this.canvasStream) === null || _a === void 0 ? void 0 : _a.getVideoTracks()[0]) === null || _b === void 0 ? void 0 : _b.readyState) === "live"
                ? new MediaStream(this.canvasStream)
                : new MediaStream();
        const audioTrack = (_c = this.streamIn) === null || _c === void 0 ? void 0 : _c.getAudioTracks()[0];
        if (audioTrack) {
            this.streamOut.addTrack(audioTrack);
        }
        log("emiting output stream", this.streamOut);
        this.events.emit("output", this.streamOut);
    }
    loadWasm(assetsPath) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tflite = yield createTFLiteModule({
                publicPath: assetsPath,
                simd: Device.isWasmSimdSupported,
            });
            log("loaded wasm assets");
            return !!this.tflite;
        });
    }
    loadModel(assetsPath) {
        return __awaiter(this, void 0, void 0, function* () {
            const modelResponse = yield fetch(`${assetsPath}/${this.modelFileName}`);
            const model = yield modelResponse.arrayBuffer();
            if (this.hasUnloaded)
                console.warn("Auvious background filter has been unloaded while still loading.");
            const modelBufferOffset = this.tflite._getModelBufferMemoryOffset();
            this.tflite.HEAPU8.set(new Uint8Array(model), modelBufferOffset);
            this.tflite._loadModel(model.byteLength);
            log("loaded tensorflow models");
        });
    }
    loadPipeline() {
        return __awaiter(this, void 0, void 0, function* () {
            let error;
            if (!this.streamIn) {
                return false;
            }
            const videoTrack = this.streamIn.getVideoTracks()[0];
            if (!videoTrack || videoTrack.readyState === "ended") {
                log("background received no video track or one with state " +
                    (videoTrack === null || videoTrack === void 0 ? void 0 : videoTrack.readyState));
                this.genOutput();
                return false;
            }
            const { width, height, frameRate } = videoTrack.getSettings();
            this.video.srcObject = new MediaStream([videoTrack]);
            this.video.autoplay = true;
            this.video.width = width;
            this.video.height = height;
            this.targetTimePerFrame = Math.floor(1000 / (frameRate || 24));
            yield new Promise((resolve) => this.video.addEventListener("loadeddata", resolve, { once: true }));
            this.anchor = this.getTime();
            this.canvas = document.createElement("canvas");
            // will not capture stream unless context is defined, firefox also throws
            this.canvas.getContext("2d");
            this.canvas.width = this.video.width;
            this.canvas.height = this.video.height;
            this.canvasStream = this.canvas.captureStream(Math.floor(frameRate || 24));
            // try it once, if it fails, then only use canvas
            if (this.useWebGL && this.segmentationConfig.pipeline === "webgl2") {
                try {
                    this.loadWebGLPipeline();
                }
                catch (ex) {
                    error = ex;
                }
            }
            if (error || !this.pipeline) {
                try {
                    this.loadCanvasPipeline();
                }
                catch (ex) {
                    error = ex;
                }
            }
            if (error) {
                this.events.emit("fail", new AnyError(error));
                return false;
            }
            else {
                this.genOutput();
                return true;
            }
        });
    }
    loadWebGLPipeline() {
        var _a;
        (_a = this.pipeline) === null || _a === void 0 ? void 0 : _a.cleanUp();
        this.pipeline = null;
        this.segmentationConfig.pipeline = "webgl2";
        this.pipeline = buildWebGL2Pipeline({
            htmlElement: this.video,
            width: this.video.width,
            height: this.video.height,
        }, this.image, this.backgroundConfig, this.segmentationConfig, this.canvas, this.tflite);
        this.pipeline.updatePostProcessingConfig(this.postProcessingConfig);
        log("webgl2 pipeline loaded");
    }
    loadCanvasPipeline() {
        var _a;
        (_a = this.pipeline) === null || _a === void 0 ? void 0 : _a.cleanUp();
        this.pipeline = null;
        this.segmentationConfig.pipeline = "canvas2dCpu";
        this.pipeline = buildCanvas2dPipeline({
            htmlElement: this.video,
            width: this.video.width,
            height: this.video.height,
        }, this.image, this.backgroundConfig, this.segmentationConfig, this.canvas, this.tflite);
        this.pipeline.updatePostProcessingConfig(this.postProcessingConfig);
        log("canvas2dCpu pipeline loaded");
    }
    whenImageReady(image) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (image.naturalHeight && image.naturalWidth) {
                    return null;
                }
                /*
                else if (image.src) {
                  await image.decode();
                } else {
                  await new Promise((resolve) =>
                    image.addEventListener("load", resolve, { once: true })
                  );
                }
                */
                if (!image.complete) {
                    yield new Promise((resolve, reject) => {
                        image.addEventListener("load", resolve, { once: true });
                        image.addEventListener("error", reject, { once: true });
                    });
                }
                // Decode after it's loaded (safe to do now)
                yield image.decode();
                return image.naturalHeight && image.naturalWidth
                    ? null
                    : new Error("Image has zero dimensions");
            }
            catch (error) {
                return error;
            }
        });
    }
    /**
     * Set a background image
     * @param image
     * @returns boolean if succeeded, also triggers onfail event if not
     */
    setImage(image) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const isImageReady = yield this.whenImageReady(image);
            if (isImageReady instanceof Error) {
                this.events.emit("fail", new ImageUnusable(isImageReady));
                throw isImageReady;
            }
            this.image = image;
            this.backgroundConfig.type = "image";
            (_a = this.pipeline) === null || _a === void 0 ? void 0 : _a.setImage(image);
            (_b = this.pipeline) === null || _b === void 0 ? void 0 : _b.updatePostProcessingConfig(this.postProcessingConfig);
            return true;
        });
    }
    unsetImage() {
        var _a, _b;
        if (this.backgroundConfig.type !== "image") {
            return;
        }
        this.image = null;
        this.backgroundConfig.type = "blur";
        (_a = this.pipeline) === null || _a === void 0 ? void 0 : _a.unsetImage();
        (_b = this.pipeline) === null || _b === void 0 ? void 0 : _b.updatePostProcessingConfig(this.postProcessingConfig);
    }
    unload() {
        var _a;
        this.hasUnloaded = true;
        this.pause();
        this.tflite = null;
        (_a = this.pipeline) === null || _a === void 0 ? void 0 : _a.cleanUp();
        this.pipeline = null;
        this.canvasStream = null;
        this.video.srcObject = null;
        this.events.emit("unload");
    }
    frameEnd() {
        this.counter++;
        const time = this.getTime();
        if (time - this.anchor >= 1000) {
            this.events.emit("fps", Math.trunc((this.counter * 1000) / (time - this.anchor)));
            this.counter = 0;
            this.anchor = time;
        }
    }
    getTime() {
        return Date.now();
    }
    render() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.rendering) {
                return;
            }
            if (!this.pipeline) {
                if (!(yield this.loadPipeline())) {
                    this.rendering = false;
                    return;
                }
                this.rendering = true;
            }
            try {
                const start = this.getTime();
                this.pipeline.render();
                // this.frameEnd();
                const end = this.getTime();
                const delay = end - start;
                this.timers.setTimeout(() => this.render(), Math.max(0, this.targetTimePerFrame - delay));
            }
            catch (ex) {
                log("blur-filter", ex);
                this.rendering = false;
                this.events.emit("fail", new AnyError(ex));
            }
        });
    }
}
